import React from "react";
import { Link } from "react-router-dom";
import { MapPin, Phone } from "lucide-react";
import { serviceAreas, businessInfo, images } from "../mock";
import { flagshipCities } from "../data/flagshipCities";

// map city display name → URL slug for /public-adjuster-:slug
const citySlug = (name) =>
  name
    .toLowerCase()
    .replace(/\s+&\s+/g, "-")
    .replace(/\s+n\s+/g, "-n-")
    .replace(/\s+/g, "-");

const CityLink = ({ city }) => (
  <Link
    to={`/public-adjuster-${citySlug(city)}`}
    aria-label={`Public Adjuster in ${city}, FL`}
    className="bg-[#0F0F0F] hover:bg-[#E11D26] hover:text-white transition-colors p-4 flex items-center gap-2 text-white/85 group"
  >
    <MapPin className="h-4 w-4 text-[#E11D26] group-hover:text-white shrink-0" />
    <span className="text-sm font-bold tracking-wide uppercase">{city}</span>
  </Link>
);

const ServiceAreas = () => {
  const flagshipAreas = serviceAreas.filter(
    (c) => flagshipCities[citySlug(c)]
  );
  const otherAreas = serviceAreas.filter((c) => !flagshipCities[citySlug(c)]);
  return (
    <section
      id="service-areas"
      className="relative py-20 md:py-28 bg-[#0A0A0A] overflow-hidden"
    >
      <div className="absolute inset-0">
        <img
          src={images.tampa}
          alt="Tampa Bay area skyline at dusk"
          className="w-full h-full object-cover opacity-25"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0A0A0A] via-[#0A0A0A]/85 to-[#0A0A0A]" />
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 grid lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-5">
          <div className="flex items-center gap-3 mb-4">
            <span className="h-[2px] w-10 bg-[#E11D26]" />
            <span className="text-[#E11D26] text-xs sm:text-sm font-bold tracking-[0.25em] uppercase">
              Service Areas
            </span>
          </div>
          <h2
            className="text-white text-4xl sm:text-5xl md:text-6xl font-black uppercase leading-[0.95]"
            style={{ fontFamily: "'Anton', 'Oswald', sans-serif" }}
          >
            Based In <span className="text-[#E11D26]">Riverview</span>.<br />
            Fighting For All Of <span className="text-[#E11D26]">Tampa Bay</span>.
          </h2>
          <p className="mt-6 text-white/75 leading-relaxed">
            Our public adjusters live and work in the Tampa Bay area. We know
            the local building codes, the hurricane history, and how Florida
            insurers operate. When disaster strikes your home or business, we
            can be on-site fast.
          </p>
          <div className="mt-6 flex flex-col sm:flex-row gap-4">
            <div className="flex items-center gap-3 text-white">
              <div className="h-10 w-10 rounded-full bg-[#E11D26]/15 border border-[#E11D26]/40 flex items-center justify-center text-[#E11D26]">
                <MapPin className="h-5 w-5" />
              </div>
              <div className="text-sm">
                <div className="font-bold">Headquartered in</div>
                <div className="text-white/70">
                  Riverview, FL · {businessInfo.address.region}
                </div>
              </div>
            </div>
            <a
              href={`tel:${businessInfo.phoneRaw}`}
              className="flex items-center gap-3 text-white hover:text-[#E11D26] transition-colors"
            >
              <div className="h-10 w-10 rounded-full bg-[#E11D26]/15 border border-[#E11D26]/40 flex items-center justify-center text-[#E11D26]">
                <Phone className="h-5 w-5" />
              </div>
              <div className="text-sm">
                <div className="font-bold">Call 24/7</div>
                <div className="text-white/70">{businessInfo.phone}</div>
              </div>
            </a>
          </div>
        </div>

        <div className="lg:col-span-7">
          <div className="text-[#E11D26] text-xs font-bold tracking-[0.2em] uppercase mb-3">
            Flagship City Guides
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-px bg-white/10 border border-white/10">
            {flagshipAreas.map((city) => (
              <CityLink key={city} city={city} />
            ))}
          </div>
          <div className="text-[#E11D26]/80 text-xs font-bold tracking-[0.2em] uppercase mt-6 mb-3">
            Also Serving
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-px bg-white/10 border border-white/10">
            {otherAreas.map((city) => (
              <CityLink key={city} city={city} />
            ))}
          </div>
          <p className="mt-4 text-white/55 text-xs tracking-wide">
            Plus all surrounding Hillsborough &amp; Pasco County communities.
          </p>
        </div>
      </div>
    </section>
  );
};

export default ServiceAreas;
